import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BuildPartnerCard from '../BuildPartnerCard/BuildPartnerCard';
import TestimonyAccordion from '../PartnerTestimony/TestimonyAccordion';
import { getPartnerTestimony } from '../../data/partnerTestimony';
import { fetchPartnerBySlug } from '../../services/partners';
import './PartnerDirectory.css';
import './Backyard.css';

export default function PartnerDetail() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const testimony = getPartnerTestimony(slug);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    fetchPartnerBySlug(slug)
      .then((result) => {
        if (!result) {
          setNotFound(true);
          setPartner(null);
        } else {
          setPartner(result);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="partner-directory font-bai bg-warm-50 min-h-[50vh] flex items-center justify-center">
        <p className="text-warm-600 text-sm">Loading builder profile…</p>
      </div>
    );
  }

  if (notFound || !partner) {
    return (
      <div className="partner-directory font-bai bg-warm-50 min-h-[50vh] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold text-warm-900 mb-2">Partner not found</h1>
        <p className="text-warm-700 mb-6">We couldn&apos;t find a build partner with that ID.</p>
        <Link to="/partners" className="partner-detail-back">
          ← All partners
        </Link>
      </div>
    );
  }

  return (
    <div className="partner-directory font-bai bg-warm-50">
      <Helmet>
        <title>{partner.name} — Construx Build Partner</title>
        <meta
          name="description"
          content={`${partner.name} — ${partner.roleLabel}. A Construx build partner serving ${partner.locationLabel}.`}
        />
      </Helmet>

      <section className="backyard-page py-12 md:py-16">
        <div className="by-wrap max-w-6xl mx-auto px-6 md:px-10">
          <Link to="/partners" className="partner-detail-back">
            ← All partners
          </Link>
          <div className="mb-8">
            <p className="text-sm font-medium uppercase tracking-widest text-[var(--clay)] mb-2">
              Build partner · {partner.locationLabel}
            </p>
            <h1 className="by-h2 text-[var(--ink)]">{partner.name}</h1>
          </div>
          <BuildPartnerCard partner={partner} stamp="Construx Build Partner" />
        </div>
      </section>

      {slug === 'austin-remodel-pro' && (
        <section className="px-6 md:px-12 py-12 md:py-16 border-t border-warm-200">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-[var(--clay)] mb-1">
              Builds · Guest house · ADU
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-warm-900 mb-6">
              Live Build, <span style={{ color: 'var(--clay)' }}>Finished ADU.</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-[3px]">
              {[
                '/why/live-project/lp-01.webp',
                '/why/live-project/lp-02.webp',
                '/why/live-project/lp-03.webp',
                '/why/live-project/lp-04.webp',
                '/why/live-project/lp-05.webp',
                '/why/live-project/lp-06.webp',
                '/why/live-project/lp-07.webp',
                '/why/live-project/lp-09.webp',
                '/why/live-project/lp-10.webp',
                '/why/live-project/lp-11.webp',
              ].map((src, i) => (
                <div key={i} className="overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={src}
                    alt={`ADU build photo ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {testimony ? (
        <section className="py-16 md:py-20 px-6 md:px-12 max-w-6xl mx-auto">
          <div className="mb-8">
            <p className="text-sm font-medium uppercase tracking-widest text-construx-red mb-2">
              {testimony.subtitle}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-warm-900 mb-3">{testimony.title}</h2>
          </div>
          <TestimonyAccordion items={testimony.items} />
        </section>
      ) : null}

      <section className="bg-white border-t border-warm-200 py-14 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <Link
            to="/interest"
            className="inline-block px-6 py-3 bg-construx-red text-white rounded-lg font-medium transition-all duration-300 hover:bg-construx-red-dark hover:shadow-btn"
          >
            Come build with us
          </Link>
        </div>
      </section>
    </div>
  );
}
